import type { ConfirmedBooking } from "@albertine/design-system/components/chat";
import type { TaggedContext } from "@albertine/types";
import type {
    CatalogItem,
    ChatMessageAttachment,
    MemberBooking,
} from "@albertine/types/openapi/concierge-v1";
import { Lib } from "albertine-shared-web";

type Translate = (key: string, params?: Record<string, string | number>) => string;

export function toArticleContextSearch(
    article: CatalogItem,
    t: Translate,
): Extract<TaggedContext, { type: "article" }> {
    return {
        type: "article",
        id: article.id,
        article: {
            title: article.name,
            imageUrl: article.imageUrls[0],
        },
        message: t("article_cta__chat_message", {
            title: article.name,
        }),
    };
}

export function toBookingContextSearch(
    booking: MemberBooking,
    t: Translate,
): Extract<TaggedContext, { type: "booking" }> {
    return {
        type: "booking",
        id: booking.id,
        booking: {
            title: booking.title ?? "",
            imageUrl: booking.croppedImageURL ?? booking.imageURL,
            optionId: booking.options?.[0]?.id,
            optionTitle: booking.options?.[0]?.title,
        },
        message: getBookingPrefillMessage(booking, t),
    };
}

export function taggedContextToAttachments(
    context: TaggedContext | undefined,
): ChatMessageAttachment[] {
    if (context?.type !== "article") {
        return [];
    }

    return [
        {
            type: "catalog-item",
            id: context.id,
            name: context.article.title,
            keyDetail: null,
            imageUrl: context.article.imageUrl ?? null,
        },
    ];
}

export function taggedContextToConfirmedBookings(
    context: TaggedContext | undefined,
): ConfirmedBooking[] | undefined {
    if (context?.type !== "booking") {
        return undefined;
    }

    return [
        {
            id: context.id,
            title: context.booking.title,
            imageUrl: context.booking.imageUrl,
            options:
                context.booking.optionId && context.booking.optionTitle
                    ? [
                          {
                              id: context.booking.optionId,
                              title: context.booking.optionTitle,
                          },
                      ]
                    : undefined,
        },
    ];
}

export function plainTextToMessageHtml(message: string): string {
    return `<p>${escapeHtml(message).replaceAll("\n", "<br/>")}</p>`;
}

function getBookingPrefillMessage(booking: MemberBooking, t: Translate): string {
    const { bookingStartDate, bookingStartTime } =
        Lib.Utils.TextFormatter.booking.startDateAndTime(
            booking.startTime,
            booking.startTimezone,
            undefined,
        );

    const numberOfGuests = Lib.Utils.TextFormatter.booking.numberOfGuests(
        booking.numberOfGuests,
    );

    const selectedOptionTitle = booking.options?.[0]?.title;
    const optionsString = selectedOptionTitle
        ? `\n\n${t(
              "booking_details__confirm_prefilled_message--with-preferred-option",
              {
                  option: selectedOptionTitle,
              },
          )}`
        : "";

    let translationKey = "booking_details__confirm_prefilled_message";
    const translationParams: Record<string, string> = {
        title: booking.title ?? "",
        date: bookingStartDate ?? "",
        time: bookingStartTime ?? "",
        numberOfGuests: numberOfGuests ?? "",
    };

    if (bookingStartTime && numberOfGuests) {
        translationKey += "--with-datetime-and-guests";
    } else if (bookingStartTime) {
        translationKey += "--with-datetime";
    } else if (numberOfGuests) {
        translationKey += "--with-guests";
    }

    return t(translationKey, translationParams) + optionsString;
}

function escapeHtml(value: string): string {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}
